import { z } from 'zod';
import { BAND_LABEL } from './badges.js';
import { CLIENT_LIFECYCLE } from './clients.js';
import { INDUSTRIES } from './controls.js';
import { AgentProviderSpec } from './llm-providers.js';
import { ASSESSMENT_STATUSES } from './portfolio.js';
import { SCORE_BANDS } from './scoring.js';
/**
 * The underwriter assistant (task 74, decision 0051). Drafts only, asked for by the underwriter:
 * - a question about the book is read as the clients list's own filters, and answered by that list under
 *   the person's own access, so every client and number shown comes from the data;
 * - a memo draft for one assessment, from its facts; every control and number in it is checked.
 */
export const ASSISTANT_PLAN_VERSION = 'assistant-book/v1';
export const ASSISTANT_MEMO_VERSION = 'assistant-memo/v1';
/** At most this many clients come back with an answer; the list has the rest. */
export const BOOK_ANSWER_ROWS = 24;
export const BookQuestion = z.object({
    question: z.string().trim().min(3, 'Ask a question about your clients').max(500, 'At most 500 characters'),
});
const STATUS_WORDS = {
    in_progress: 'In progress: still being answered',
    submitted: 'Submitted, waiting for review',
    in_review: 'In review',
    in_remediation: 'Changes requested from the business',
    decided: 'Decided',
    closed: 'Closed',
};
const LIFECYCLE_WORDS = {
    prospect: 'Prospect',
    assessing: 'Assessing',
    quoted: 'Quoted',
    bound: 'Bound: on cover',
    lapsed: 'Lapsed',
};
const choices = (pairs) => pairs.map(([value, label]) => ({ value, label }));
/** The values each clients-list filter takes, as the assistant is told them. */
export const BOOK_OPTIONS = {
    industry: choices(Object.entries(INDUSTRIES)),
    band: choices(SCORE_BANDS.map((b) => [b.key, `${BAND_LABEL[b.key]} (${b.min}–${b.max})`])),
    status: choices(ASSESSMENT_STATUSES.map((s) => [s, STATUS_WORDS[s]])),
    renewing: choices([['overdue', 'Renewal date passed'], ['30', 'Renewing within 30 days'], ['60', 'Renewing within 60 days'], ['90', 'Renewing within 90 days']]), // prettier-ignore
    attention: choices([['exceptions', 'With exceptions: kill switches not in place'], ['provisional', 'Provisional score'], ['unscored', 'Not scored yet']]), // prettier-ignore
    lifecycle: choices(CLIENT_LIFECYCLE.map((l) => [l, LIFECYCLE_WORDS[l]])),
    sort: choices([['updated', 'Recent activity'], ['name', 'Name'], ['score', 'CyberScore'], ['progress', 'Progress'], ['assessments', 'Assessments'], ['renewal', 'Renewal date'], ['premium', 'Premium']]), // prettier-ignore
};
const Choice = z.strictObject({ value: z.string(), label: z.string() });
/** What the core sends the agent. */
export const BookPlanRequest = z.strictObject({
    tenant_id: z.string(),
    question: z.string().min(3).max(500),
    today: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    options: z.strictObject({
        industry: z.array(Choice),
        band: z.array(Choice),
        status: z.array(Choice),
        renewing: z.array(Choice),
        attention: z.array(Choice),
        lifecycle: z.array(Choice),
        sort: z.array(Choice),
    }),
    provider: AgentProviderSpec.nullable().optional(),
});
export const BOOK_FILTERS = ['industry', 'band', 'status', 'renewing', 'attention', 'lifecycle'];
/** The value of the agent's `book:plan` proposal. The core checks every filter again. */
export const AgentBookPlan = z.object({
    reading: z.string().max(200),
    filters: z.partialRecord(z.enum(BOOK_FILTERS), z.string()),
    archived: z.boolean(),
    name: z.string().max(80),
    sort: z.string(),
    dir: z.enum(['asc', 'desc']),
    how: z.enum(['model', 'keywords']),
});
const rm = (n) => `RM ${Math.round(n).toLocaleString('en-MY')}`;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** A client row as an answer shows it. */
export function bookClient(r) {
    const s = r.latest?.score ?? null;
    return {
        customer_key: r.customer_key,
        customer_name: r.customer_name,
        industry: r.industry,
        lifecycle: r.lifecycle,
        renewal_date: r.renewal_date,
        premium: r.premium,
        assessment_id: r.latest?.id ?? null,
        score: s?.score ?? null,
        band: s?.band ?? null,
        exceptions: s?.exceptions ?? 0,
    };
}
/** Plain facts about the clients shown, worked out here and not by a model. */
export function bookFacts(total, clients) {
    if (!total)
        return ['No clients match.'];
    const facts = [total > clients.length ? `${plural(total, 'client')} match; the first ${clients.length} are shown.` : `${plural(total, 'client')} match.`]; // prettier-ignore
    const scored = clients.filter((c) => c.score !== null);
    if (scored.length)
        facts.push(`Average CyberScore ${Math.round(scored.reduce((n, c) => n + c.score, 0) / scored.length)} across the ${plural(scored.length, 'scored client')} shown.`); // prettier-ignore
    const exceptions = clients.filter((c) => c.exceptions > 0).length;
    if (exceptions)
        facts.push(`${plural(exceptions, 'client')} shown with exceptions (kill switches not in place).`);
    const premiums = clients.filter((c) => c.premium !== null);
    if (premiums.length)
        facts.push(`${rm(premiums.reduce((n, c) => n + c.premium, 0))} in premium across the ${plural(premiums.length, 'client')} shown with one.`); // prettier-ignore
    const next = clients
        .filter((c) => c.renewal_date)
        .sort((a, b) => (a.renewal_date < b.renewal_date ? -1 : 1))[0];
    if (next)
        facts.push(`Next renewal shown: ${next.customer_name}, ${next.renewal_date}.`);
    return facts;
}
// ---------- memo drafts ----------
const MemoControl = z.strictObject({
    control: z.string(),
    name: z.string(),
    status: z.string().default(''),
    points: z.number().int().nullable().optional(),
});
export const MemoFacts = z.strictObject({
    business: z.string(),
    industry: z.string().nullable().optional(),
    status: z.string(),
    score: z.number().int(),
    band: z.string(),
    provisional: z.boolean(),
    cap: z.number().int().nullable().optional(),
    evidence_confidence: z.number(),
    evidence_grade: z.string(),
    evidence_reasons: z.array(z.string()).max(8).default([]),
    kill_switches_failed: z.array(MemoControl).max(20).default([]),
    weakest: z.array(MemoControl).max(12).default([]),
    strongest: z.array(MemoControl).max(12).default([]),
    top_actions: z.array(MemoControl).max(8).default([]),
    open_conditions: z
        .array(z.strictObject({
        title: z.string(),
        control: z.string().nullable().optional(),
        due: z.string().nullable().optional(),
        state: z.string().default(''),
    })) // prettier-ignore
        .max(20)
        .default([]),
    conflicts: z.array(z.strictObject({ control: z.string(), state: z.string(), reading: z.string() })).max(20).default([]), // prettier-ignore
    claims: z.record(z.string(), z.number().int()).default({}),
    premium: z.number().nullable().optional(),
    renewal_date: z.string().nullable().optional(),
});
export const MemoRequest = z.strictObject({
    tenant_id: z.string(),
    assessment_id: z.string(),
    facts: MemoFacts,
    provider: AgentProviderSpec.nullable().optional(),
});
/** The value of the agent's `memo:<assessment>` proposal. */
export const AgentMemo = z.object({
    summary: z.string().min(1).max(1200),
    strengths: z.array(z.string().max(300)).max(4),
    concerns: z.array(z.string().max(300)).max(5),
    conditions: z
        .array(z.object({ title: z.string().max(200), control: z.string().nullable(), reason: z.string().max(300) }))
        .max(4),
    cited: z.array(z.string()),
    how: z.enum(['model', 'template']),
    problems: z.array(z.string()).max(10),
});
//# sourceMappingURL=assistant.js.map